import { useState, useMemo, useCallback } from 'react';
import {
  INITIAL_DIAGRAM_EDITOR_STATE,
  cleanSvgMarkup,
  getDiagramType,
  getDiagramCode,
  normalizeSvgForDisplay,
  renderTikzToSvgWithFallback,
  buildSvgPreviewDoc,
  buildTikzPreviewDoc,
  isEmojiDiagram,
  getEmojiDiagramGrid,
  emojiGridToRows,
  emojiRowsToGrid,
} from '../utils/diagramEditor';
import { cacheSvg } from '../utils/tikzSvgCache';

/**
 * Manages the shared diagram-editing workflow (state, TikZ/SVG conversion,
 * previews and save) for the EditDiagramModal. Consumers decide how the saved
 * diagram is persisted via the `onApplyDiagram(questionIndex, diagram)` callback.
 */
export default function useDiagramEditor({ onApplyDiagram }) {
  const [diagramEditor, setDiagramEditor] = useState(INITIAL_DIAGRAM_EDITOR_STATE);
  const [diagramEditorError, setDiagramEditorError] = useState('');
  const [isSavingDiagram, setIsSavingDiagram] = useState(false);

  const closeDiagramEditor = useCallback(() => {
    setDiagramEditor(INITIAL_DIAGRAM_EDITOR_STATE);
    setDiagramEditorError('');
  }, []);

  const openDiagramEditor = useCallback((question, questionIndex) => {
    const diagramType = getDiagramType(question);
    const diagramCode = getDiagramCode(question);
    const isEmoji = isEmojiDiagram(question);
    const emojiRows = isEmoji ? emojiGridToRows(getEmojiDiagramGrid(question.diagram)) : [];
    let initialEditorMode = diagramType === 'svg' ? 'svg' : 'tikz';
    if (isEmoji) initialEditorMode = 'emoji';
    // Show the already-rendered SVG (svg_cache) in the SVG tab so both the TikZ
    // source and its rendered SVG are available while editing. cleanSvgMarkup
    // strips backslash-escaped quotes the AI sometimes emits in the SVG body —
    // without it the preview iframe would render a blank box.
    const existingSvg = cleanSvgMarkup(
      diagramType === 'svg'
        ? diagramCode
        : (typeof question?.diagram === 'object' ? (question.diagram?.svg_cache || '') : '')
    );

    setDiagramEditorError('');
    setDiagramEditor({
      isOpen: true,
      questionIndex,
      questionNumber: question?.question_number || (questionIndex + 1),
      // Use initialEditorMode as the gate so any non-svg type (tikz, latex, chemfig,
      // etc.) always lands its code in the TikZ textarea, not silently dropped.
      tikzCode: initialEditorMode === 'tikz' ? diagramCode : '',
      originalTikzCode: initialEditorMode === 'tikz' ? diagramCode : '',
      emojiRows,
      originalEmojiRows: emojiRows,
      emojiBaseDiagram: isEmoji ? question.diagram : null,
      emojiIsFlatList: isEmoji
        && Array.isArray(question.diagram.labels)
        && !question.diagram.labels.some((row) => Array.isArray(row)),
      svgCode: existingSvg,
      originalSvgCode: existingSvg,
      editorMode: initialEditorMode,
      excalidrawOpen: false,
      excalidrawElements: question?.diagram?.excalidraw_elements || null,
      excalidrawFiles: question?.diagram?.excalidraw_files || null,
    });
  }, []);

  const handleRegenerateSvg = useCallback(async () => {
    if (!diagramEditor.tikzCode) {
      setDiagramEditorError('Enter TikZ code to regenerate SVG.');
      return;
    }
    setDiagramEditorError('');
    const updatedSvg = await renderTikzToSvgWithFallback(diagramEditor.tikzCode);
    if (!updatedSvg) {
      setDiagramEditorError('TikZ-to-SVG conversion failed. Please review the TikZ code.');
      return;
    }
    const normalized = normalizeSvgForDisplay(updatedSvg);
    // Refresh the shared cache so other surfaces (DiagramTile, the worksheet
    // HTML re-renderer) pick up the freshly rendered SVG for this TikZ
    // source on their next read.
    cacheSvg(diagramEditor.tikzCode, normalized);
    setDiagramEditor((prev) => ({
      ...prev,
      svgCode: normalized,
      editorMode: 'svg'
    }));
  }, [diagramEditor.tikzCode]);

  const handleSaveDiagram = useCallback(async () => {
    if (diagramEditor.questionIndex === null || diagramEditor.questionIndex === undefined) {
      setDiagramEditorError('Diagram context is missing. Please reopen editor and try again.');
      return;
    }

    setDiagramEditorError('');
    setIsSavingDiagram(true);

    if (diagramEditor.editorMode === 'emoji') {
      const grid = emojiRowsToGrid(diagramEditor.emojiRows);
      if (grid.length === 0) {
        onApplyDiagram(diagramEditor.questionIndex, null);
      } else {
        // Keep the original emoji diagram fields (description, font size,
        // libraries, ...) and its flat-list shape when it is still one row.
        const labels = diagramEditor.emojiIsFlatList && grid.length === 1 ? grid[0] : grid;
        onApplyDiagram(diagramEditor.questionIndex, {
          font_size_pt: 12,
          libraries: 'docx_table',
          ...(diagramEditor.emojiBaseDiagram || {}),
          type: 'emoji',
          code: null,
          labels,
        });
      }
      setIsSavingDiagram(false);
      closeDiagramEditor();
      return;
    }

    const isTikzMode = diagramEditor.editorMode === 'tikz';
    const rawCode = isTikzMode ? diagramEditor.tikzCode : diagramEditor.svgCode;

    // Clearing the code removes the diagram from the question.
    if (!rawCode || !rawCode.trim()) {
      onApplyDiagram(diagramEditor.questionIndex, null);
      setIsSavingDiagram(false);
      closeDiagramEditor();
      return;
    }

    let diagramObj;
    if (isTikzMode) {
      // Keep TikZ as the editable source of truth and attach a rendered SVG
      // (svg_cache) so display can use stable SVG instead of compiling TikZ.
      //
      // When the TikZ source has been edited since the editor opened, the
      // svgCode loaded from the existing svg_cache is now STALE and must be
      // regenerated — otherwise the saved diagram pairs new TikZ with the
      // pre-edit rendered SVG and the display shows the wrong picture.
      const tikzEdited = diagramEditor.tikzCode !== diagramEditor.originalTikzCode;
      let svg = diagramEditor.svgCode;
      if (tikzEdited || !svg) {
        const rendered = await renderTikzToSvgWithFallback(diagramEditor.tikzCode);
        if (tikzEdited) {
          // Even if render failed, don't fall back to the stale pre-edit
          // SVG — better to save with no svg_cache (display will live-render
          // the new TikZ) than persist a mismatched picture.
          svg = rendered || '';
        } else if (rendered) {
          svg = rendered;
        }
      }
      diagramObj = { type: 'tikz', code: diagramEditor.tikzCode };
      if (svg) {
        const normalized = normalizeSvgForDisplay(svg);
        diagramObj.svg_cache = normalized;
        // Refresh the shared cache so subscribers (worksheet display, other
        // open tiles) swap to the new SVG immediately.
        cacheSvg(diagramEditor.tikzCode, normalized);
      }
    } else {
      diagramObj = { type: 'svg', code: normalizeSvgForDisplay(diagramEditor.svgCode) };
    }
    if (diagramEditor.excalidrawElements?.length) {
      diagramObj.excalidraw_elements = diagramEditor.excalidrawElements;
    }
    if (diagramEditor.excalidrawFiles) {
      diagramObj.excalidraw_files = diagramEditor.excalidrawFiles;
    }

    onApplyDiagram(diagramEditor.questionIndex, diagramObj);
    setIsSavingDiagram(false);
    closeDiagramEditor();
  }, [diagramEditor, onApplyDiagram, closeDiagramEditor]);

  const svgPreviewDoc = useMemo(
    () => buildSvgPreviewDoc(diagramEditor.svgCode),
    [diagramEditor.svgCode]
  );

  const tikzPreviewDoc = useMemo(
    () => buildTikzPreviewDoc(diagramEditor.tikzCode),
    [diagramEditor.tikzCode]
  );

  return {
    diagramEditor,
    setDiagramEditor,
    diagramEditorError,
    setDiagramEditorError,
    isSavingDiagram,
    openDiagramEditor,
    closeDiagramEditor,
    handleRegenerateSvg,
    handleSaveDiagram,
    svgPreviewDoc,
    tikzPreviewDoc,
  };
}
